/**
 * Favorites Management Component
 * Admin/Team Leader interface for managing product favorites.
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * All available products list
 */
const PRODUCTS_LIST = [
  'Tax Search', 'Update Tax Search', 'MLS', 'MLS Update', 'HOA Finder', 'Utility Search',
  'Current Owner Search', 'Two Owner Search', 'Full Search', 'AVR Search', 'Deed Search',
  'Mortgage Search', 'Legal and Vesting', 'O&E', 'Commercial Full Search', 'Commercial COS'
];

/**
 * Favorites Management Component
 * Allows admin/team leaders to configure favorite products for Order Desk
 */
export const FavoritesManagement = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');

  useEffect(() => {
    fetchFavorites();
    initializeFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/favorites`);
      setFavorites(response.data.favorites || []);
    } catch (error) {
      toast.error('Error fetching favorites');
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeFavorites = async () => {
    try {
      await axios.post(`${API}/admin/favorites/initialize`);
      fetchFavorites();
    } catch (error) {
      console.log('Favorites may already be initialized');
    }
  };

  const handleAddFavorite = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      await axios.post(`${API}/admin/favorites`, {
        product_name: selectedProduct,
        order_index: favorites.length + 1
      });
      
      toast.success(`Added "${selectedProduct}" to favorites`);
      setShowAddModal(false);
      setSelectedProduct('');
      fetchFavorites();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Product is already in favorites');
      } else {
        toast.error('Error adding to favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (favoriteId, productName) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/admin/favorites/${favoriteId}`);
      toast.success(`Removed "${productName}" from favorites`);
      fetchFavorites();
    } catch (error) {
      toast.error('Error removing favorite');
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (draggedId, targetIndex) => {
    const newFavorites = [...favorites];
    const draggedIndex = newFavorites.findIndex(f => f.id === draggedId);
    
    if (draggedIndex === -1) return;
    
    const [draggedItem] = newFavorites.splice(draggedIndex, 1);
    newFavorites.splice(targetIndex, 0, draggedItem);
    
    setFavorites(newFavorites);

    try {
      const orderedIds = newFavorites.map(f => f.id);
      await axios.post(`${API}/admin/favorites/reorder`, {
        product_ids: orderedIds
      });
      toast.success('Favorites reordered successfully');
    } catch (error) {
      toast.error('Error reordering favorites');
      fetchFavorites();
    }
  };

  const getAvailableProducts = () => {
    const favoriteNames = favorites.map(f => f.product_name);
    return PRODUCTS_LIST.filter(product => !favoriteNames.includes(product));
  };

  return (
    <div className="space-y-6" data-testid="favorites-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Manage Favorites</h2>
          <p className="text-slate-600">Configure which products appear in the Favorites section for Order Desk</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} disabled={loading} data-testid="add-favorite-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Favorite
        </Button>
      </div>

      {/* Current Favorites */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current Favorites (Drag to Reorder)</CardTitle>
          <CardDescription>
            These products will appear in the Favorites section when creating new orders
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-slate-500">Loading favorites...</div>
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No favorites configured. Click "Add Favorite" to get started.
            </div>
          ) : (
            <div className="space-y-2">
              {favorites.map((favorite, index) => (
                <div
                  key={favorite.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 cursor-move"
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', favorite.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const draggedId = e.dataTransfer.getData('text/plain');
                    handleReorder(draggedId, index);
                  }}
                  data-testid={`favorite-item-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-slate-400 text-sm font-mono">#{index + 1}</div>
                    <div className="font-medium text-slate-900">{favorite.product_name}</div>
                    <Badge variant="secondary" className="text-xs">
                      ⭐ Favorite
                    </Badge>
                  </div>
                  
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleRemoveFavorite(favorite.id, favorite.product_name)}
                    disabled={loading}
                    data-testid={`remove-favorite-${index}`}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Preview</CardTitle>
          <CardDescription>
            This is how the favorites will appear in the Order Desk form
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium text-yellow-700">⭐ FAVORITES</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {favorites.map((favorite) => (
                <div
                  key={favorite.id}
                  className="p-3 rounded-lg border-2 border-blue-500 bg-blue-50 text-blue-900 text-left transition-all duration-200 relative"
                >
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                    <span className="text-xs">⭐</span>
                  </div>
                  <div className="font-medium text-sm">
                    {favorite.product_name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Favorite Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Product to Favorites</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger data-testid="select-product-trigger">
                  <SelectValue placeholder="Choose a product to add to favorites" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableProducts().map((product) => (
                    <SelectItem key={product} value={product}>
                      {product}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={handleAddFavorite} disabled={!selectedProduct || loading} data-testid="confirm-add-favorite">
                Add to Favorites
              </Button>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { PRODUCTS_LIST };
export default FavoritesManagement;
